import {Component, Input, OnChanges} from '@angular/core';
import {FragmentIndexService} from 'app/fragment/indexing/fragment-index.service';
import {
  ClauseType,
  DocumentFragment,
  Fragment,
  FragmentType,
  InternalReferenceType,
  SectionFragment,
} from 'app/fragment/types';
import {InternalDocumentReferenceFragment} from 'app/fragment/types/reference/internal-document-reference-fragment';
import {InternalInlineReferenceFragment} from 'app/fragment/types/reference/internal-inline-reference-fragment';
import {ReferenceSortingUtils} from 'app/services/references/reference-utils/reference-sorting-utils';
import {ReferenceSummaryRow} from '../types/reference-summary-row';

export interface ReferenceSummaryLink {
  displayText: string;
  targetUrl: string;
}

export interface ReferenceSummaryExpandedRow {
  targetType: string;
  targetContent: ReferenceSummaryLink;
  valid: boolean;
  inlineReferences: ReferenceSummaryLink[];
}

@Component({
  selector: 'cars-reference-summary-expanded-table',
  templateUrl: './reference-summary-expanded-table.component.html',
  styleUrls: ['./reference-summary-expanded-table.component.scss'],
})
export class ReferenceSummaryExpandedTableComponent implements OnChanges {
  private static readonly HEADING_CLAUSE_TYPES: Readonly<ClauseType[]> = [
    ClauseType.HEADING_1,
    ClauseType.HEADING_2,
    ClauseType.HEADING_3,
  ];

  @Input() public referenceSummaryRow: ReferenceSummaryRow;
  @Input() public fullDocumentTree: DocumentFragment;

  public expandedRows: ReferenceSummaryExpandedRow[] = [];
  public excludeValidReferences: boolean = true;

  private _allExpandedRows: ReferenceSummaryExpandedRow[] = [];

  constructor(private _fragmentIndexService: FragmentIndexService) {}

  public ngOnChanges(): void {
    if (!this.fullDocumentTree) {
      return;
    }

    const internalDocumentReferences: InternalDocumentReferenceFragment[] = ReferenceSortingUtils.getSortedReferences(
      this.fullDocumentTree
        .getReferenceSections()
        .reduce(
          (references: InternalDocumentReferenceFragment[], section: SectionFragment) =>
            references.concat(
              section.children[0].children
                .filter((ref) => ref.is(FragmentType.INTERNAL_DOCUMENT_REFERENCE))
                .map((ref) => ref as InternalDocumentReferenceFragment)
            ),
          []
        )
        .filter((internalDocumentReference) =>
          internalDocumentReference.targetDocumentId.equals(this.referenceSummaryRow.targetDocumentId)
        )
    );

    const inlineReferencesByDocumentReferenceId: Record<string, InternalInlineReferenceFragment[]> = {};
    this.fullDocumentTree
      .getSections()
      .filter((s) => !s.deleted)
      .forEach((s) =>
        s.iterateDown(null, null, (frag: Fragment) => {
          if (frag.is(FragmentType.INTERNAL_INLINE_REFERENCE)) {
            const inlineRef = frag as InternalInlineReferenceFragment;
            if (!inlineReferencesByDocumentReferenceId[inlineRef.internalDocumentReferenceId.value]) {
              inlineReferencesByDocumentReferenceId[inlineRef.internalDocumentReferenceId.value] = [];
            }
            inlineReferencesByDocumentReferenceId[inlineRef.internalDocumentReferenceId.value].push(inlineRef);
          }
        })
      );

    this._allExpandedRows = internalDocumentReferences.map(
      (internalDocumentReference: InternalDocumentReferenceFragment) =>
        this._generateReferenceRow(internalDocumentReference, inlineReferencesByDocumentReferenceId)
    );
    this._setFilteredRows();
  }

  private _generateReferenceRow(
    internalDocumentReference: InternalDocumentReferenceFragment,
    inlineReferencesByDocumentReferenceId: Record<string, InternalInlineReferenceFragment[]>
  ): ReferenceSummaryExpandedRow {
    return {
      targetType: this._getReferenceTypeDisplayText(internalDocumentReference.internalReferenceType),
      targetContent: this._getReferenceTarget(internalDocumentReference),
      valid: this._getReferenceIsValid(internalDocumentReference),
      inlineReferences: this._getInternalReferenceLocationsData(
        internalDocumentReference,
        inlineReferencesByDocumentReferenceId
      ),
    };
  }

  private _getReferenceTypeDisplayText(internalReferenceType: InternalReferenceType): string {
    switch (internalReferenceType) {
      case InternalReferenceType.SECTION_REFERENCE:
        return 'Section';
      case InternalReferenceType.WSR_REFERENCE:
        return 'WSR';
      case InternalReferenceType.CLAUSE_REFERENCE:
        return 'Clause';
    }
  }

  /**
   * Calculates the reference target display and url for the provided internal document reference.
   */
  private _getReferenceTarget(internalDocumentReference: InternalDocumentReferenceFragment): ReferenceSummaryLink {
    const targetUrl: string = `/documents/${internalDocumentReference.targetDocumentId.value}/sections/${internalDocumentReference.targetSectionId.value}`;
    switch (internalDocumentReference.internalReferenceType) {
      case InternalReferenceType.SECTION_REFERENCE:
        return {
          displayText: `${internalDocumentReference.sectionIndex} ${internalDocumentReference.sectionTitle}`,
          targetUrl: targetUrl,
        };
      case InternalReferenceType.WSR_REFERENCE:
        return {
          displayText: `${internalDocumentReference.wsrCode}`,
          targetUrl: targetUrl,
        };
      case InternalReferenceType.CLAUSE_REFERENCE:
        if (
          ReferenceSummaryExpandedTableComponent.HEADING_CLAUSE_TYPES.includes(
            internalDocumentReference.targetClauseType
          )
        ) {
          return {
            displayText: `Heading "${internalDocumentReference.targetFragmentValue}" in Section ${internalDocumentReference.sectionIndex}`,
            targetUrl: targetUrl,
          };
        }
        return {
          displayText: `${internalDocumentReference.targetFragmentIndex}`,
          targetUrl: targetUrl,
        };
    }
  }

  private _getReferenceIsValid(internalDocumentReference: InternalDocumentReferenceFragment): boolean {
    if (
      !this.referenceSummaryRow.targetVersionIsLive &&
      !!this.referenceSummaryRow.lastPublishedInvalidDocumentReferenceIds
    ) {
      return !this.referenceSummaryRow.lastPublishedInvalidDocumentReferenceIds.some((id) =>
        id.equals(internalDocumentReference.id)
      );
    } else {
      return !this.referenceSummaryRow.liveInvalidDocumentReferenceIds.some((id) =>
        id.equals(internalDocumentReference.id)
      );
    }
  }

  private _getInternalReferenceLocationsData(
    internalDocumentReference: InternalDocumentReferenceFragment,
    inlineReferencesByDocumentReferenceId: Record<string, InternalInlineReferenceFragment[]>
  ): ReferenceSummaryLink[] {
    const inlineReferences: InternalInlineReferenceFragment[] =
      inlineReferencesByDocumentReferenceId[internalDocumentReference.id.value];

    if (!inlineReferences) {
      return [];
    }

    return inlineReferences.map((inlineRef) => {
      const clause: Fragment = inlineRef.findAncestorWithType(FragmentType.CLAUSE);
      const clauseIndex: string = this._fragmentIndexService.getIndex(clause.id);
      const targetUrl: string = `/documents/${inlineRef.documentId.value}/sections/${inlineRef.sectionId.value}`;
      if (clauseIndex?.length > 0) {
        return {
          displayText: clauseIndex,
          targetUrl: targetUrl,
        };
      }
      const section: Fragment = clause.findAncestorWithType(FragmentType.SECTION);
      return {
        displayText: `In section: ${this._fragmentIndexService.getIndex(inlineRef.sectionId)} ${section.value}`,
        targetUrl: targetUrl,
      };
    });
  }

  public toggleExcludeValidReferences(): void {
    this.excludeValidReferences = !this.excludeValidReferences;
    this._setFilteredRows();
  }

  private _setFilteredRows(): void {
    this.expandedRows = this._allExpandedRows.filter((row) => !row.valid || !this.excludeValidReferences);
  }
}
